/**
 * The funnel's device-side reports (`D258`, `D260`), fire-and-forget like
 * `onboarding/report-step.ts`: never awaited, never retried, never queued in
 * the outbox, every failure swallowed. A lost report is one missing row; a
 * share must never wait on one.
 *
 * - `share_offered` / `share_completed` go through `POST /me/events`,
 *   authenticated: the API stamps the account from the token.
 * - `link_opened` goes to `POST /share/link-opened`, public and anonymous —
 *   sent at boot, before any session exists — as `text/plain`, so it needs no
 *   preflight from the site's origin. The API records it only while links are
 *   on.
 */
import type { ApiClient } from "@repo/client/api";
import {
  type LinkOpenedInput,
  PRODUCT_EVENTS_ROUTE,
  type ProductEventInput,
  SHARE_LINK_OPENED_ROUTE,
  type ShareCardKind,
  type ShareFormat,
} from "@repo/contract";
import { API_URL } from "../../data/api";

/** The OTA bundle running now — the `app_version` column (`D238`). */
const BUNDLE_VERSION: string = import.meta.env.VITE_BUNDLE_VERSION ?? "dev";

function post(
  api: Pick<ApiClient, "authorizedFetch">,
  body: ProductEventInput
) {
  api
    .authorizedFetch(PRODUCT_EVENTS_ROUTE, {
      body: JSON.stringify(body),
      headers: { "content-type": "application/json" },
      keepalive: true,
      method: "POST",
    })
    .catch(() => undefined);
}

/** The app put a card in front of the person unasked. */
export function reportShareOffered(
  api: Pick<ApiClient, "authorizedFetch">,
  kind: ShareCardKind
): void {
  post(api, { appVersion: BUNDLE_VERSION, event: "share_offered", kind });
}

export interface CompletedShare {
  readonly channel: string;
  readonly format: ShareFormat;
  readonly kind: ShareCardKind;
  /** Only while links are on (`D257`). */
  readonly ref: string | null;
}

/** The card left the device — as far as the platform says. */
export function reportShareCompleted(
  api: Pick<ApiClient, "authorizedFetch">,
  share: CompletedShare
): void {
  post(api, {
    appVersion: BUNDLE_VERSION,
    channel: share.channel,
    event: "share_completed",
    format: share.format,
    kind: share.kind,
    ...(share.ref === null ? {} : { ref: share.ref }),
  });
}

/** This page was opened through a share link. */
export function reportLinkOpened(
  ref: string,
  fetcher: typeof fetch = fetch
): void {
  const body: LinkOpenedInput = { appVersion: BUNDLE_VERSION, ref };
  fetcher(`${API_URL}${SHARE_LINK_OPENED_ROUTE}`, {
    body: JSON.stringify(body),
    headers: { "content-type": "text/plain;charset=UTF-8" },
    keepalive: true,
    method: "POST",
  }).catch(() => undefined);
}
